import { resolveApiUrl } from '@/shared/config/api.constants';
import { env } from '@/shared/config/env';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
}

export class ApiRequestError extends Error {
  override readonly message: string;
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(message: string, status: number, code: string, details: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.message = message;
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** True when the API refused access (403), e.g. a record outside the member's service sites. */
export function isForbiddenError(err: unknown): err is ApiRequestError {
  return err instanceof ApiRequestError && err.status === 403;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
  error: { code: string; details: unknown } | null;
}

function buildUrl(path: string): string {
  return resolveApiUrl(env.apiBaseUrl, path);
}

function isMutatingMethod(method: HttpMethod): boolean {
  return method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(`(?:^|; )${escaped}=([^;]*)`).exec(document.cookie);
  const value = match?.[1];
  if (!value) {
    return null;
  }
  return decodeURIComponent(value);
}

async function ensureCsrfToken(signal?: AbortSignal): Promise<string | null> {
  const existing = readCookie('csrftoken');
  if (existing) {
    return existing;
  }
  try {
    await fetch(buildUrl('/api/v1/auth/csrf/'), {
      method: 'GET',
      credentials: 'include',
      signal,
    });
  } catch {
    return null;
  }
  return readCookie('csrftoken');
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', headers = {}, body, signal } = options;
  const csrfToken = isMutatingMethod(method) ? await ensureCsrfToken(signal) : null;
  const res = await fetch(buildUrl(path), {
    method,
    signal,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const rawText = await res.text();
  if (!rawText) {
    if (res.ok) {
      return null as T;
    }
    throw new ApiRequestError(`HTTP ${res.status}: empty response`, res.status, 'parse_error', null);
  }

  let json: ApiEnvelope<T>;
  try {
    json = JSON.parse(rawText) as ApiEnvelope<T>;
  } catch {
    throw new ApiRequestError(
      `HTTP ${res.status}: ${res.statusText || 'invalid response'}`,
      res.status,
      'parse_error',
      rawText,
    );
  }

  if (!json.success || json.error) {
    const code = json.error?.code ?? 'unknown';
    const message = json.message || res.statusText;
    throw new ApiRequestError(message, res.status, code, json.error?.details ?? null);
  }

  return json.data;
}

function parseContentDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) {
    return quoted[1];
  }
  const unquoted = /filename=([^;]+)/i.exec(header);
  return unquoted?.[1]?.trim() ?? null;
}

export async function apiDownload(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await fetch(buildUrl(path), {
    method: 'GET',
    credentials: 'include',
    signal: options.signal,
  });

  if (!res.ok) {
    const rawText = await res.text();
    try {
      const json = JSON.parse(rawText) as ApiEnvelope<unknown>;
      throw new ApiRequestError(
        json.message || res.statusText,
        res.status,
        json.error?.code ?? 'unknown',
        json.error?.details ?? null,
      );
    } catch (error) {
      if (error instanceof ApiRequestError) {
        throw error;
      }
      throw new ApiRequestError(`HTTP ${res.status}`, res.status, 'download_failed', rawText);
    }
  }

  const blob = await res.blob();
  return { blob, filename: parseContentDisposition(res.headers.get('Content-Disposition')) };
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export interface ApiFormDataRequestOptions {
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: FormData;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Fallback total when the browser cannot compute upload length. */
  uploadTotalBytes?: number;
  onUploadProgress?: (progress: { loaded: number; total: number }) => void;
  /** Fired once all request bytes have been sent; the server may still be processing. */
  onUploadBytesComplete?: () => void;
}

/** Multipart request (e.g. file upload). Do not set Content-Type — the browser sets the boundary. */
export async function apiRequestFormData<T>(path: string, options: ApiFormDataRequestOptions): Promise<T> {
  const method = options.method ?? 'POST';
  const csrfToken = await ensureCsrfToken(options.signal);
  const timeoutMs = options.timeoutMs;

  const data = await new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, buildUrl(path), true);
    xhr.withCredentials = true;
    if (csrfToken) {
      xhr.setRequestHeader('X-CSRFToken', csrfToken);
    }
    if (options.signal) {
      if (options.signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      options.signal.addEventListener(
        'abort',
        () => {
          xhr.abort();
        },
        { once: true },
      );
    }
    if (timeoutMs && timeoutMs > 0) {
      xhr.timeout = timeoutMs;
    }
    let uploadBytesCompleteFired = false;
    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable ? event.total : (options.uploadTotalBytes ?? 0);
      if (total <= 0) {
        return;
      }
      if (options.onUploadProgress) {
        options.onUploadProgress({ loaded: event.loaded, total });
      }
      if (
        !uploadBytesCompleteFired &&
        options.onUploadBytesComplete &&
        event.loaded >= total
      ) {
        uploadBytesCompleteFired = true;
        options.onUploadBytesComplete();
      }
    };
    xhr.onload = () => {
      const rawText = xhr.responseText;
      if (!rawText) {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(null as T);
          return;
        }
        reject(new ApiRequestError(`HTTP ${xhr.status}: empty response`, xhr.status, 'parse_error', null));
        return;
      }
      let json: ApiEnvelope<T>;
      try {
        json = JSON.parse(rawText) as ApiEnvelope<T>;
      } catch {
        reject(
          new ApiRequestError(
            `HTTP ${xhr.status}: ${xhr.statusText || 'invalid response'}`,
            xhr.status,
            'parse_error',
            rawText,
          ),
        );
        return;
      }
      if (!json.success || json.error) {
        const code = json.error?.code ?? 'unknown';
        const message = json.message || xhr.statusText;
        reject(new ApiRequestError(message, xhr.status, code, json.error?.details ?? null));
        return;
      }
      resolve(json.data);
    };
    xhr.onerror = () => {
      reject(new ApiRequestError('Network error during upload', 0, 'network_error', null));
    };
    xhr.ontimeout = () => {
      reject(new ApiRequestError('Upload timed out', 0, 'timeout', null));
    };
    xhr.onabort = () => {
      reject(new DOMException('Aborted', 'AbortError'));
    };
    xhr.send(options.body);
  });

  return data;
}

export interface BlobUploadOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  onUploadProgress?: (progress: { loaded: number; total: number }) => void;
}

/** Upload a file directly to a presigned URL (e.g. Azure Blob SAS). */
export async function uploadBlobWithProgress(
  file: File,
  uploadUrl: string,
  contentType: string,
  options?: BlobUploadOptions,
): Promise<void> {
  const timeoutMs = options?.timeoutMs;

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    if (options?.signal) {
      if (options.signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      options.signal.addEventListener(
        'abort',
        () => {
          xhr.abort();
        },
        { once: true },
      );
    }
    if (timeoutMs && timeoutMs > 0) {
      xhr.timeout = timeoutMs;
    }
    xhr.upload.onprogress = (event) => {
      if (options?.onUploadProgress && event.lengthComputable) {
        options.onUploadProgress({ loaded: event.loaded, total: event.total });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new ApiRequestError(
          `Upload failed (HTTP ${xhr.status})`,
          xhr.status,
          'upload_failed',
          xhr.responseText || null,
        ),
      );
    };
    xhr.onerror = () => {
      reject(new ApiRequestError('Network error during upload', 0, 'network_error', null));
    };
    xhr.ontimeout = () => {
      reject(new ApiRequestError('Upload timed out', 0, 'timeout', null));
    };
    xhr.onabort = () => {
      reject(new DOMException('Aborted', 'AbortError'));
    };
    xhr.send(file);
  });
}
